import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';

import { setPage } from '~actions/transferActions';

import { primaryColor, greyDark } from '~theme';
import { Tags, Select, Gap } from '~ui-kit';

import UpdateButton from './UpdateButton';
import DateFilter from './components/DateFilter';

import {
  statusOptions,
  sortOptions,
  TASKLIST_START_DATE,
  TASKLIST_END_DATE,
} from '../../reducers/transferReducers';
import {
  setSearchTerm,
  setStartDate,
  setEndDate,
  setAdvanced,
  setCategory,
  setMultiCategory,
  setStatus,
  setSort,
  setUnit,
  // setInitialized,
  resetFilters,
  // setPageOffset,
  setCostCenter,
} from '../../actions/transferActions';

const TaskListFilters = props => {
  const { isSearched } = props;
  // const queryOrderNumber = queryString.parse(location.search)?.ordernumber;
  const [initialized, setInitialized] = useState(false);
  // const initialized = useSelector(state => state.transferReducers.initialized);
  const advanced = useSelector(state => state.transferReducers.advanced);
  const unit = useSelector(state => state.transferReducers.unit);
  const costCenter = useSelector(state => state.transferReducers.costCenter);
  const category = useSelector(state => state.transferReducers.category);
  const multiCategory = useSelector(
    state => state.transferReducers.multiCategory
  );
  const status = useSelector(state => state.transferReducers.status);
  const sort = useSelector(state => state.transferReducers.sort);
  const searchTerm = useSelector(state => state.transferReducers.searchTerm);
  const startDate = useSelector(state => state.transferReducers.startDate);
  const endDate = useSelector(state => state.transferReducers.endDate);

  const dispatch = useDispatch();
  const isDesktop = useSelector(state => state.reducer.isDesktop);

  // Get categories
  const inventoryCategories = useSelector(
    state => state.reducer.inventoryCategories
  );
  const multiCategoryOptions = inventoryCategories.map(category => ({
    key: category.id,
    value: category.id,
    label: category.name,
    payload: [category.id],
  }));
  const categoryOptions = [
    {
      key: 'ALL',
      value: 'ALL',
      label: 'Kaikki',
      payload: inventoryCategories.map(category => category.id),
    },
    ...multiCategoryOptions,
  ];

  // Get units
  const userData = useSelector(state => state.user.data);
  const userUnit = userData.ordererOrganization;
  const allUnits = [userUnit, ...userData.followedOrganizations].map(unit => ({
    key: unit.id,
    value: unit.id,
    label: `Tilauslista ${unit.name}`,
    type: 'UNIT',
    payload: [unit.id],
  }));

  // The variable name is a bit misleading now.
  // There can be cost centers here as well.
  const unitOptions = [
    {
      key: 'ALL',
      value: 'ALL',
      label: 'Kaikki tilaukset',
      type: 'UNIT',
      payload: allUnits.map(unit => unit.value),
    },
    {
      key: 'OWN',
      value: 'OWN',
      label: 'Omat tilaukset',
      type: 'UNIT',
      payload: [],
    },
    {
      key: 'OWN_CC',
      value: 'OWN_CC',
      label: `Kustannuspaikan ${userData.ordererOrganization?.costCentreCode} tilaukset`,
      type: 'COST_CENTER',
      payload: [userData.ordererOrganization?.costCentreId],
    },
    ...allUnits,
  ];

  const toggleAdvanced = () => {
    // Clear search term and dates because they're not visible in normal view
    if (advanced) {
      dispatch(setSearchTerm(''));
      dispatch(setStartDate(TASKLIST_START_DATE));
      dispatch(setEndDate(TASKLIST_END_DATE));
    }
    dispatch(setAdvanced(!advanced));
  };

  const search = () => {
    if (!initialized) {
      // setInitialized(true);
      return;
    }
    dispatch(setPage(0));
  };

  // Search whenever a parameter is adjusted
  // TODO add debouncer maybe to search field directly
  useEffect(search, [
    category,
    multiCategory,
    status,
    sort,
    unit,
    costCenter,
    searchTerm,
    startDate,
    endDate,
    advanced,
    initialized,
  ]);

  // Initialize values that depend on Redux values here
  // This is a bit inelegant, but we can't really do this in the reducer.
  // We reset the Redux state here on component mount to emulate local state,
  // but we get the added benefit of the state being in Redux so we can
  // fetch tasks from anywhere. This can be easily reverted if needed
  // by moving `initialized` to Redux state also.
  useEffect(() => {
    if (!initialized) {
      // Reset to initial state
      dispatch(resetFilters());
      // Replace more difficult default values
      dispatch(setUnit(unitOptions[0]));
      dispatch(setCategory(categoryOptions[0]));
      dispatch(setMultiCategory(multiCategoryOptions));
      setInitialized(true);
    }
  }, []);

  if (advanced) {
    return (
      <Wrapper>
        <HeaderRow>
          <Header>Tilauslista</Header>
          <UpdateButton />
        </HeaderRow>
        <Link mobile={!isDesktop} margin onClick={toggleAdvanced}>
          <ChevronLeft icon={faChevronLeft} />{' '}
          <LinkText mobile={!isDesktop}>
            Palaa yksinkertaisempaan hakuun
          </LinkText>
        </Link>
        <Cluster>
          <Row>
            <ControlWrapper>
              <SearchLabel>Tilausnro</SearchLabel>
              <Control>
                <SearchField
                  value={searchTerm}
                  className="form-control"
                  onChange={event =>
                    dispatch(setSearchTerm(event.target.value))
                  }
                />
              </Control>
            </ControlWrapper>
          </Row>
          <Row>
            <ControlWrapper>
              <DateLabel>Aikaväli</DateLabel>
              <Control>
                <DateFilter
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={v => dispatch(setStartDate(v))}
                  setEndDate={v => dispatch(setEndDate(v))}
                  disabled={isSearched}
                />
              </Control>
            </ControlWrapper>
          </Row>
          <Row>
            <ControlWrapper>
              <Label>Palvelut</Label>
              <Control>
                <Select
                  placeholder="Valitse yksi tai useampi palvelu..."
                  value={multiCategory}
                  isMulti
                  name="categories"
                  options={categoryOptions}
                  onChange={options => {
                    // If user clicks KAIKKI, include everything
                    if (options.find(option => option.value === 'ALL')) {
                      dispatch(setMultiCategory(multiCategoryOptions));
                    } else {
                      dispatch(setMultiCategory(options));
                    }
                  }}
                />
              </Control>
            </ControlWrapper>
          </Row>
          <Row>
            <StatusControlWrapper>
              <StatusLabel>Vaihe</StatusLabel>
              <Control>
                <Tags
                  multi
                  options={statusOptions}
                  value={status}
                  onChange={value => {
                    dispatch(setStatus(value));
                  }}
                />
              </Control>
            </StatusControlWrapper>
          </Row>
        </Cluster>
        {/* <StyledButton w="200px" bgColor={green} onClick={search}>
          Hae tilauksia
        </StyledButton> */}
        <Gap height="40px" />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <HeaderRow>
        <Header>Tilauslista</Header>
        <UpdateButton />
      </HeaderRow>
      <Controls>
        {/* First row */}
        <CategoryRow>
          <CategoryWrapper>
            <Label>Tilaukset</Label>
            <Control>
              <FilterSelect
                options={unitOptions}
                value={unit || costCenter}
                onChange={value => {
                  // Value can be either a cost center or a unit (list)
                  if (value.type === 'COST_CENTER') {
                    dispatch(setUnit(null));
                    dispatch(setCostCenter(value));
                  } else if (value.type === 'UNIT') {
                    dispatch(setUnit(value));
                    dispatch(setCostCenter(null));
                  } else {
                    console.error('Unknown unit filter dropdown type', value);
                  }
                }}
              />
            </Control>
          </CategoryWrapper>
        </CategoryRow>
        {/* Second row */}
        <CategoryRow>
          <CategoryWrapper>
            <Label>Palvelut</Label>
            <Control>
              <FilterSelect
                options={categoryOptions}
                value={category}
                onChange={value => {
                  dispatch(setCategory(value));
                }}
              />
            </Control>
          </CategoryWrapper>
          <Link mobile={!isDesktop} onClick={toggleAdvanced}>
            <LinkText mobile={!isDesktop}>Siirry tarkempaan hakuun</LinkText>
            <ChevronRight icon={faChevronRight} />
          </Link>
        </CategoryRow>
        <Row>
          <StatusControlWrapper>
            <StatusLabel>Vaihe</StatusLabel>
            <Control>
              <Tags
                multi
                options={statusOptions}
                value={status}
                onChange={value => {
                  dispatch(setStatus(value));
                  // Filter here
                }}
              />
            </Control>
          </StatusControlWrapper>
        </Row>
        <SortRow>
          <SortWrapper>
            <SortLabel>Järjestys</SortLabel>
            <Control>
              <Select
                id="sort-select"
                options={sortOptions}
                value={sort}
                onChange={value => {
                  dispatch(setSort(value));
                }}
              />
            </Control>
          </SortWrapper>
        </SortRow>
      </Controls>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 10px;
`;

const CategoryRow = styled(Row)`
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const Cluster = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  // max-width: 475px;
`;

const ControlWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex-grow: ${props => props.grow || '1'};
`;

const CategoryWrapper = styled(ControlWrapper)`
  flex-grow: 1;
  max-width: 400px;
`;

const FilterSelect = styled(Select)`
  // display: inline-block;
  // max-width: 300px;
`;

const Label = styled.label`
  display: block;
  flex-shrink: 0;
  width: 75px;
  margin-top: 12px;
  color: ${props => (props.grey ? greyDark : primaryColor)};
`;

const SortLabel = styled(Label)`
  width: unset;
  margin-right: 8px;
`;

const StatusLabel = styled(Label)`
  margin-top: 8px;
`;

const SearchLabel = styled(Label)`
  margin-top: 7px;
`;

const DateLabel = styled(Label)`
  margin-top: 9px;
`;

const Control = styled.div`
  flex-grow: 1;
`;

const Link = styled.div`
  color: #253c7e;
  cursor: pointer;
  margin-bottom: ${props => (props.margin ? '40px' : '0px')};

  display: flex;
  align-items: center;
`;

const LinkText = styled.div`
  text-decoration: ${props => (props.mobile ? 'none' : 'underline')};
`;

const SearchField = styled.input`
  height: 30px;
  width: 100%;
`;

const ChevronRight = styled(FontAwesomeIcon)`
  margin-left: 8px;
`;

const ChevronLeft = styled(FontAwesomeIcon)`
  margin-right: 8px;
`;

// const StyledButton = styled(Button)`
//   margin-top: 20px;
// `;

const Header = styled.h2`
  color: var(--primaryColor);
  margin-bottom: 40px;
`;

const SortWrapper = styled.div`
  display: flex;
`;

const SortRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const StatusControlWrapper = styled(ControlWrapper)`
  flex-wrap: wrap;
  gap: 8px;
`;

export default TaskListFilters;
